import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Clock, Check, X, User, FolderKanban, Calendar, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import FeatureTip from '../../components/FeatureTip';
import PinPromptModal from '../../components/PinPromptModal';
import { shouldPromptForPin } from '../../utils/pinGate';
import { fmtDateTime, fmtTime } from '../../utils/dateTime';

const emptyForm = {
  project_id: '',
  date: new Date().toISOString().slice(0, 10),
  start_time: '08:00',
  end_time: '17:00',
  break_minutes: 30,
  description: '',
  machine_id: '',
  machine_hours: '',
};

const TimeReports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [forUserId, setForUserId] = useState('');
  const [saving, setSaving] = useState(false);
  // Fas 5 (#8): flaggade utstämplingar (tidig/sen)
  const [flagged, setFlagged] = useState([]);
  const [handled, setHandled] = useState([]);
  const [showHandled, setShowHandled] = useState(false);
  const [machines, setMachines] = useState([]);
  const [approving, setApproving] = useState(null);  // entry id under approval
  // Iter36: Redigera en tidrapport
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Simon-feedback: sortering + filtrering av tidrapporter
  const [filters, setFilters] = useState({
    status: 'all',        // 'all' | 'pending' | 'approved' | 'rejected'
    project_id: 'all',    // 'all' | project.id
    employee_id: 'all',   // 'all' | employee.id
    date_from: '',        // 'YYYY-MM-DD'
    date_to: '',
    search: '',           // fritext på kommentar/anställdsnamn
    sort: 'date_desc',    // 'date_desc' | 'date_asc' | 'hours_desc' | 'hours_asc'
    source: 'all',        // 'all' | 'from_clock' | 'manual'
  });

  const resetFilters = () => setFilters({
    status: 'all', project_id: 'all', employee_id: 'all',
    date_from: '', date_to: '', search: '', sort: 'date_desc', source: 'all',
  });

  // PIN-gate state (Simon: PIN vid godkännande, en gång per session)
  const [pinPrompt, setPinPrompt] = useState({ open: false, reason: '', onOk: null });
  // Iter49: räkna gamla dubbelrader (labor-TR från stämplingar med maskin)
  const [machineLinkedCount, setMachineLinkedCount] = useState(0);
  const [cleaningUp, setCleaningUp] = useState(false);

  const refreshMachineLinkedCount = async () => {
    try {
      const r = await api.countMachineLinkedTimeReports();
      setMachineLinkedCount(r.data?.count || 0);
    } catch {
      // tyst — best-effort
    }
  };

  const handleCleanupMachineLinked = async () => {
    if (!window.confirm(
      `Ta bort ${machineLinkedCount} dubbelrader (arbetsrader som är kopplade till maskin)?\n\n`
      + 'Täcker BÅDA fallen:\n'
      + '  • Stämplingar där arbetaren också körde maskin\n'
      + '  • Manuellt registrerade tidrapporter där en matchande maskinlogg finns på samma dag/anställd/projekt\n\n'
      + 'Enligt svensk anläggningspraxis inkluderar maskinens timtaxa arbetstiden — dessa arbetsrader ska bort. '
      + 'Tidrapporterna behålls för lönespårning men markeras som "absorberade av maskin" och tas bort från fakturan.\n\n'
      + 'Går inte att ångra.'
    )) return;
    setCleaningUp(true);
    try {
      const r = await api.cleanupMachineLinkedTimeReports();
      toast.success(r.data?.message || 'Städning klar');
      await refreshMachineLinkedCount();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Städning misslyckades');
    } finally {
      setCleaningUp(false);
    }
  };

  // Wrapper: kör action direkt om PIN redan är verifierad, annars visa PIN-modal
  const withPin = async (reason, action) => {
    const needs = await shouldPromptForPin();
    if (!needs) {
      action();
      return;
    }
    setPinPrompt({ open: true, reason, onOk: action });
  };

  const handleStatusChange = async (r, newStatus) => {
    const doIt = async () => {
      setUpdatingStatus(r.id);
      try {
        await api.updateTimeReportStatus(r.id, newStatus);
        toast.success(newStatus === 'approved' ? 'Tidrapport godkänd' : 'Tidrapport avvisad');
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Kunde inte uppdatera status');
      } finally {
        setUpdatingStatus(null);
      }
    };
    // Endast approved kräver PIN — reject / öppna igen behöver det inte
    if (newStatus === 'approved') {
      withPin(`godkänna tidrapport för ${empName(r.employee_id)}`, doIt);
    } else {
      doIt();
    }
  };

  useEffect(() => { fetchAll(); refreshMachineLinkedCount(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, p, e, f, h, m] = await Promise.all([
        api.getTimeReports(),
        api.getProjects(),
        api.getEmployees().catch(() => ({ data: [] })),
        api.listFlaggedClockEntries().catch(() => ({ data: { entries: [] } })),
        api.listHandledClockEntries(30).catch(() => ({ data: { entries: [] } })),
        api.getMachines().catch(() => ({ data: [] })),
      ]);
      setReports(r.data || []);
      setProjects((p.data || []).filter(x => !x.archived));
      setEmployees(e.data || []);
      setFlagged(f.data?.entries || []);
      setHandled(h.data?.entries || []);
      setMachines(m.data || []);
    } catch {
      toast.error('Kunde inte hämta tidrapporter');
    } finally { setLoading(false); }
  };

  const handleRevertApproval = async (entry) => {
    if (!window.confirm(`Ångra beslutet för ${entry.user_name}? Stämplingen läggs tillbaka i väntan-panelen.`)) return;
    try {
      await api.revertClockApproval(entry.id);
      toast.success('Beslut ångrat');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ångra');
    }
  };

  const handleOvertimeApproval = async (entry, status) => {
    const doIt = async () => {
      setApproving(entry.id);
      try {
        await api.overtimeApproval(entry.id, { status });
        toast.success(status === 'approved' ? 'Övertid godkänd' : 'Övertid avvisad — arbetstiden justerad');
        fetchAll();
      } catch (err) {
        toast.error(formatApiError(err, 'Kunde inte spara beslut'));
      } finally {
        setApproving(null);
      }
    };
    if (status === 'approved') {
      withPin(`godkänna flaggad stämpling för ${entry.user_name || 'anställd'}`, doIt);
    } else {
      doIt();
    }
  };

  const openEditModal = (r) => {
    // Räkna ut rast från lagrad total_hours och start/end (rast = start-end - total)
    let breakMin = 0;
    if (r.start_time && r.end_time && (r.hours != null)) {
      const [sh, sm] = r.start_time.split(':').map(Number);
      const [eh, em] = r.end_time.split(':').map(Number);
      const clockH = (eh + em / 60) - (sh + sm / 60);
      breakMin = Math.max(0, Math.round((clockH - r.hours) * 60));
    }
    setForm({
      project_id: r.project_id || '',
      date: r.date || new Date().toISOString().slice(0, 10),
      start_time: (r.start_time || '08:00').slice(0, 5),
      end_time: (r.end_time || '17:00').slice(0, 5),
      break_minutes: breakMin,
      description: r.comment || '',
    });
    setForUserId(r.employee_id || '');
    setEditingId(r.id);
    setShowForm(true);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Ta bort tidrapport från ${r.date} (${empName(r.employee_id)})? Går inte att ångra.`)) return;
    setDeleting(r.id);
    try {
      await api.deleteTimeReport(r.id);
      toast.success('Tidrapport borttagen');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort');
    } finally {
      setDeleting(null);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setForUserId('');
  };

  const handleSave = async () => {
    if (!form.project_id) { toast.error('Välj projekt'); return; }
    if (form.start_time >= form.end_time) { toast.error('Sluttid måste vara efter starttid'); return; }
    setSaving(true);
    try {
      const [sh, sm] = form.start_time.split(':').map(Number);
      const [eh, em] = form.end_time.split(':').map(Number);
      const breakH = (parseInt(form.break_minutes) || 0) / 60;
      const hours = Math.max(0, (eh + em / 60) - (sh + sm / 60) - breakH);
      const payload = {
        project_id: form.project_id,
        employee_id: forUserId || user.id,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        hours: Math.round(hours * 100) / 100,
        comment: form.description || null,
      };
      // Maskin (endast vid nyskapande — edit-endpointen ignorerar dessa)
      if (!editingId && form.machine_id) {
        payload.machine_id = form.machine_id;
        payload.machine_hours = form.machine_hours ? parseFloat(form.machine_hours) : payload.hours;
      }
      if (editingId) {
        await api.updateTimeReport(editingId, payload);
        toast.success('Tidrapport uppdaterad');
      } else {
        await api.createTimeReport(payload);
        toast.success('Tidrapport sparad');
      }
      closeForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally { setSaving(false); }
  };

  const projectName = (id) => projects.find(p => p.id === id)?.name || '—';
  const empName = (id) => employees.find(e => e.id === id)?.name || employees.find(e => e.id === id)?.email || '—';

  // Filtrera + sortera rapporter (Simon-feedback: tusentals rader måste kunna avgränsas)
  const filteredReports = React.useMemo(() => {
    const s = (filters.search || '').trim().toLowerCase();
    let list = reports.filter((r) => {
      if (filters.status !== 'all' && r.status !== filters.status) return false;
      if (filters.project_id !== 'all' && r.project_id !== filters.project_id) return false;
      if (filters.employee_id !== 'all' && r.employee_id !== filters.employee_id) return false;
      if (filters.date_from && String(r.date) < filters.date_from) return false;
      if (filters.date_to && String(r.date) > filters.date_to) return false;
      if (filters.source === 'from_clock' && !r.source_clock_entry_id) return false;
      if (filters.source === 'manual' && r.source_clock_entry_id) return false;
      if (s) {
        const hay = `${empName(r.employee_id)} ${projectName(r.project_id)} ${r.comment || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      const ha = Number(a.total_hours ?? a.hours ?? 0);
      const hb = Number(b.total_hours ?? b.hours ?? 0);
      const da = String(a.date || '');
      const db = String(b.date || '');
      switch (filters.sort) {
        case 'date_asc': return da.localeCompare(db);
        case 'hours_desc': return hb - ha;
        case 'hours_asc': return ha - hb;
        case 'date_desc':
        default: return db.localeCompare(da);
      }
    });
    return list;
  }, [reports, filters, employees, projects]);

  return (
    <div className="space-y-6" data-testid="time-reports-page">
      <FeatureTip tipId="time-reports" title="Tidrapporter" body="Här ser du alla in- och utstämplingar från fältet. Som admin kan du även själv rapportera tid via knappen 'Lägg till tid'." />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clock size={22} className="text-[#E8613A]" /> Tidrapporter
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredReports.length === reports.length
              ? `${reports.length} rapporter`
              : `${filteredReports.length} av ${reports.length} rapporter`}
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full" data-testid="tr-new-btn">
          <Plus size={14} className="mr-1.5" /> Lägg till tid
        </Button>
      </div>

      {/* Iter49: Varningsbanner för dubbelrader (labor-TR från maskinstämplingar) */}
      {machineLinkedCount > 0 && (
        <div
          className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          data-testid="tr-machine-cleanup-banner"
        >
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900 dark:text-amber-100 flex items-center gap-1.5">
              <AlertTriangle size={16} /> {machineLinkedCount} dubbelrader hittades
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-200 mt-1 leading-snug">
              Enligt svensk anläggningspraxis inkluderar maskinens timtaxa arbetstiden — så när arbetaren
              stämplar in <strong>med maskin</strong> ska bara maskin-raden hamna på fakturan (aldrig
              en separat &quot;Arbete&quot;-rad). Klicka på knappen för att ta bort dessa dubbelrader OCH
              uppdatera berörda fakturor.
            </p>
          </div>
          <Button
            onClick={handleCleanupMachineLinked}
            disabled={cleaningUp}
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            data-testid="tr-cleanup-machine-btn"
          >
            {cleaningUp ? 'Städar…' : `Städa ${machineLinkedCount} dubbelrader`}
          </Button>
        </div>
      )}

      {/* Filter + sortering */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="tr-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</Label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm"
              data-testid="tr-filter-status"
            >
              <option value="all">Alla ({reports.length})</option>
              <option value="pending">Väntar godkännande</option>
              <option value="approved">Godkända</option>
              <option value="rejected">Avvisade</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Projekt</Label>
            <select
              value={filters.project_id}
              onChange={(e) => setFilters(f => ({ ...f, project_id: e.target.value }))}
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm"
              data-testid="tr-filter-project"
            >
              <option value="all">Alla projekt</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Anställd</Label>
            <select
              value={filters.employee_id}
              onChange={(e) => setFilters(f => ({ ...f, employee_id: e.target.value }))}
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm"
              data-testid="tr-filter-employee"
            >
              <option value="all">Alla anställda</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Sortera</Label>
            <select
              value={filters.sort}
              onChange={(e) => setFilters(f => ({ ...f, sort: e.target.value }))}
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm"
              data-testid="tr-filter-sort"
            >
              <option value="date_desc">Senaste datum först</option>
              <option value="date_asc">Äldsta datum först</option>
              <option value="hours_desc">Flest timmar först</option>
              <option value="hours_asc">Färst timmar först</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Källa</Label>
            <select
              value={filters.source}
              onChange={(e) => setFilters(f => ({ ...f, source: e.target.value }))}
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm"
              data-testid="tr-filter-source"
            >
              <option value="all">Alla källor</option>
              <option value="from_clock">Endast från stämpling</option>
              <option value="manual">Endast manuella</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Från datum</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
              className="mt-1 h-9"
              data-testid="tr-filter-date-from"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Till datum</Label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
              className="mt-1 h-9"
              data-testid="tr-filter-date-to"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Sök (kommentar, namn, projekt)</Label>
            <Input
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              placeholder="T.ex. grävning, Erik, Villa Söderberg…"
              className="mt-1 h-9"
              data-testid="tr-filter-search"
            />
          </div>
        </div>
        {(filters.status !== 'all' || filters.project_id !== 'all' || filters.employee_id !== 'all' ||
          filters.date_from || filters.date_to || filters.search || filters.sort !== 'date_desc' ||
          filters.source !== 'all') && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-[#E8613A] hover:underline font-medium"
            data-testid="tr-filter-reset"
          >
            Rensa filter
          </button>
        )}
      </div>

      {/* Fas 5 (#8): Flaggade utstämplingar — tidiga med AI-åsikt + sena som väntar godkännande */}
      {flagged.length > 0 && (
        <div className="bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-xl p-5" data-testid="flagged-clock-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-amber-600" />
            <h2 className="text-base font-bold">Flaggade stämplingar ({flagged.length})</h2>
          </div>
          <div className="space-y-3">
            {flagged.map(e => {
              const isEarly = e.early_by_min > 0;
              const isLate = e.late_by_min > 0;
              const pending = (e.admin_approval_status === 'pending' || !e.admin_approval_status);
              return (
                <div key={e.id} className="bg-card border border-border rounded-lg p-3 space-y-2" data-testid={`flagged-entry-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{e.user_name}</span>
                        {e.project_name && <span className="text-xs text-muted-foreground">— {e.project_name}</span>}
                        {isEarly && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                            Ut {e.early_by_min} min för tidigt
                          </span>
                        )}
                        {isLate && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            Övertid +{e.late_by_min} min
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {fmtDateTime(e.clock_in)}
                        {' — '}
                        {e.clock_out && fmtTime(e.clock_out)}
                        {'  ·  '}
                        Totalt: <strong>{e.total_hours}h</strong>
                      </p>
                      {isEarly && e.early_reason && (
                        <p className="text-xs mt-2 pl-3 border-l-2 border-orange-300">
                          <span className="font-semibold text-orange-700 dark:text-orange-400">Anledning: </span>
                          <span className="text-foreground">{e.early_reason}</span>
                        </p>
                      )}
                      {isEarly && e.ai_opinion && (
                        <div className="mt-2 p-2 rounded-md bg-muted/50 border border-border">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Sparkles size={11} className="text-[#E8613A]" />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#E8613A]">AI-analys</span>
                            <span className={`text-[9px] font-semibold px-1 rounded ${
                              e.ai_opinion.verdict === 'reasonable' ? 'bg-green-100 text-green-700' :
                              e.ai_opinion.verdict === 'suspicious' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {e.ai_opinion.verdict === 'reasonable' ? 'Rimlig' : e.ai_opinion.verdict === 'suspicious' ? 'Följ upp' : 'Akut'}
                            </span>
                          </div>
                          <p className="text-xs text-foreground leading-snug">{e.ai_opinion.summary}</p>
                          {e.ai_opinion.recommendation && (
                            <p className="text-[11px] text-muted-foreground mt-1 italic">→ {e.ai_opinion.recommendation}</p>
                          )}
                        </div>
                      )}
                    </div>
                    {pending && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <Button size="sm" onClick={() => handleOvertimeApproval(e, 'approved')}
                          disabled={approving === e.id}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                          data-testid={`approve-overtime-${e.id}`}>
                          <Check size={12} className="mr-1" /> Godkänn
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleOvertimeApproval(e, 'rejected')}
                          disabled={approving === e.id}
                          className="border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs"
                          data-testid={`reject-overtime-${e.id}`}>
                          <X size={12} className="mr-1" /> Avvisa
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hanterade stämplingar — historik senaste 30d, foldbar */}
      {handled.length > 0 && (
        <div className="border border-border rounded-xl" data-testid="handled-clock-panel">
          <button
            type="button"
            onClick={() => setShowHandled(!showHandled)}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium hover:bg-muted/30 transition-colors"
            data-testid="toggle-handled-clock"
          >
            <span className="flex items-center gap-2">
              <Check size={14} className="text-muted-foreground" />
              Hanterade stämplingar senaste 30 dagar ({handled.length})
            </span>
            <span className="text-xs text-muted-foreground">{showHandled ? '▲ Dölj' : '▼ Visa'}</span>
          </button>
          {showHandled && (
            <div className="border-t border-border divide-y divide-border max-h-96 overflow-y-auto">
              {handled.map(e => {
                const isEarly = e.early_by_min > 0;
                const isApproved = e.admin_approval_status === 'approved';
                return (
                  <div key={e.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`handled-entry-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="font-semibold">{e.user_name}</span>
                        {e.project_name && <span className="text-xs text-muted-foreground">— {e.project_name}</span>}
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          isApproved ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                     : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {isApproved ? 'Godkänd' : 'Avvisad'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {isEarly ? `Ut ${e.early_by_min}min för tidigt` : `Övertid +${e.late_by_min}min`}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {fmtDateTime(e.clock_in)}
                        {' — '}
                        {e.clock_out && fmtTime(e.clock_out)}
                        {' · Totalt '}
                        <strong>{e.total_hours}h</strong>
                        {e.admin_approval_at && <span> · beslut: {fmtDateTime(e.admin_approval_at)}</span>}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRevertApproval(e)}
                      className="text-xs text-[#E8613A] hover:underline whitespace-nowrap"
                      data-testid={`revert-handled-${e.id}`}
                    >
                      Ångra
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Laddar…</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm" data-testid="tr-empty">
            Inga tidrapporter ännu. Klicka på &quot;Lägg till tid&quot; för att skapa en.
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm" data-testid="tr-filtered-empty">
            Inga rapporter matchar dina filter. <button onClick={resetFilters} className="text-[#E8613A] hover:underline font-medium">Rensa filter</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-3">Datum</th>
                  <th className="text-left px-3 py-3">Anställd</th>
                  <th className="text-left px-3 py-3">Projekt</th>
                  <th className="text-left px-3 py-3">Tid</th>
                  <th className="text-right px-3 py-3">Timmar</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-right px-5 py-3">Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => openEditModal(r)} data-testid={`tr-row-${r.id}`}>
                    <td className="px-5 py-3 font-medium">{r.date}</td>
                    <td className="px-3 py-3 text-muted-foreground">{empName(r.employee_id)}</td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {projectName(r.project_id)}
                      {r.source_clock_entry_id && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          title="Denna tidrapport skapades automatiskt när arbetaren stämplade ut"
                          data-testid={`tr-from-clock-${r.id}`}
                        >
                          <Clock size={9} /> Från stämpling
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{r.start_time}–{r.end_time}</td>
                    <td className="px-3 py-3 text-right font-semibold">{(r.total_hours ?? r.hours)?.toFixed?.(1) || '—'}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        r.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        r.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}>
                        {r.status === 'approved' ? 'Godkänd' : r.status === 'rejected' ? 'Avvisad' : 'Väntar'}
                      </span>
                      {r.edited_at && (
                        <span className="ml-1.5 text-[9px] italic text-muted-foreground" title={`Redigerad ${fmtDateTime(r.edited_at)}`}>
                          · redigerad
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {r.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(r, 'approved'); }}
                            disabled={updatingStatus === r.id}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-800 mr-2 disabled:opacity-40"
                            data-testid={`tr-approve-${r.id}`}
                          >
                            <Check size={12} /> Godkänn
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(r, 'rejected'); }}
                            disabled={updatingStatus === r.id}
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 mr-3 disabled:opacity-40"
                            data-testid={`tr-reject-${r.id}`}
                          >
                            <X size={12} /> Avvisa
                          </button>
                        </>
                      )}
                      {(r.status === 'approved' || r.status === 'rejected') && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleStatusChange(r, 'pending'); }}
                          disabled={updatingStatus === r.id}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-600 mr-3 disabled:opacity-40"
                          data-testid={`tr-reopen-${r.id}`}
                        >
                          Öppna igen
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openEditModal(r); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#E8613A] hover:underline mr-3"
                        data-testid={`tr-edit-${r.id}`}
                      >
                        Redigera
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
                        disabled={deleting === r.id}
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive disabled:opacity-40"
                        data-testid={`tr-delete-${r.id}`}
                      >
                        {deleting === r.id ? 'Tar bort…' : 'Ta bort'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Redigera tidrapport' : 'Lägg till tidrapport'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Anställd</Label>
              <select value={forUserId} onChange={(e) => setForUserId(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="tr-emp-select">
                <option value="">Jag själv ({user?.name || user?.email})</option>
                {employees.filter(emp => emp.id !== user?.id).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Projekt *</Label>
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="tr-project-select">
                <option value="">Välj projekt…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Datum *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Rast (min)</Label>
                <Input type="number" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Start *</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" data-testid="tr-start" />
              </div>
              <div>
                <Label className="text-xs">Slut *</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="tr-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Beskrivning</Label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2"
                placeholder="t.ex. Grävning vid uppfart" />
            </div>
            {/* Maskinval — endast vid nyskapande. Skapar automatiskt en machine_hour-post + fakturarad */}
            {!editingId && machines.length > 0 && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                <div>
                  <Label className="text-xs">Maskin (valfri)</Label>
                  <select
                    value={form.machine_id}
                    onChange={(e) => setForm({ ...form, machine_id: e.target.value })}
                    className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2"
                    data-testid="tr-machine-select"
                  >
                    <option value="">— Ingen maskin —</option>
                    {machines.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.hourly_rate ? ` (${m.hourly_rate} kr/tim)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Maskintimmar</Label>
                  <input
                    type="number"
                    step="0.25"
                    value={form.machine_hours}
                    onChange={(e) => setForm({ ...form, machine_hours: e.target.value })}
                    placeholder="Samma som arbetstid"
                    className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2"
                    data-testid="tr-machine-hours"
                    disabled={!form.machine_id}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>Avbryt</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="tr-save-btn">
              {saving ? 'Sparar…' : (editingId ? 'Uppdatera' : 'Spara')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN-gate modal (Simon-feedback: bekräfta godkännande med PIN, en gång per session) */}
      <PinPromptModal
        open={pinPrompt.open}
        reason={pinPrompt.reason}
        onCancel={() => setPinPrompt({ open: false, reason: '', onOk: null })}
        onVerified={() => {
          const cb = pinPrompt.onOk;
          setPinPrompt({ open: false, reason: '', onOk: null });
          cb?.();
        }}
      />
    </div>
  );
};

export default TimeReports;
